<template>
  <div class="flex flex-wrap mt-4">
    <div class="w-full px-4">
      <div
        class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white border-0"
      >
        <div class="rounded-t bg-gray-100 mb-0 px-6 py-6">
          <div class="flex items-center justify-between flex-wrap gap-2">
            <h6 class="text-blueGray-700 text-xl font-bold">
              Personal Information
            </h6>
            <div v-if="form.client_code" class="flex items-center gap-2">
              <span class="text-xs font-semibold uppercase text-gray-500">Client ID:</span>
              <span class="font-mono font-bold text-sm bg-white text-gray-800 px-3 py-1 rounded border border-gray-300 shadow-sm select-all">{{ form.client_code }}</span>
            </div>
          </div>
        </div>

        <div class="flex-auto px-4 lg:px-10 py-10 pt-0">
          <form @submit.prevent="updateClient">
            <h6 class="section-title mt-3">Client Information</h6>

            <div class="flex flex-wrap">
              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">
                  Client First Name <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.first_name"
                  type="text"
                  class="form-input-primary w-full"
                  placeholder="Client First Name"
                />
              </div>

              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">Middle Name</label>
                <input
                  v-model="form.middle_name"
                  type="text"
                  class="form-input-primary w-full"
                  placeholder="Middle Name"
                />
              </div>

              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">
                  Client Last Name <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.last_name"
                  type="text"
                  class="form-input-primary w-full"
                  placeholder="Client Last Name"
                />
              </div>

              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">
                  Date of Birth <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.date_of_birth"
                  type="date"
                  class="form-input w-full"
                />
              </div>

              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">
                  SS Number <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.ss_number"
                  v-mask="'###-##-####'"
                  type="text"
                  maxlength="11"
                  class="form-input-primary w-full"
                  placeholder="123-45-6789"
                />
              </div>

              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">
                  UCI Number <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.uci_number"
                  v-mask="'##-####-####'"
                  type="text"
                  class="form-input-primary w-full"
                  placeholder="UCI Number"
                />
              </div>

              <div class="w-full lg:w-6/12 px-4 mb-3">
                <label class="field-label">
                  Gender <span class="text-red-500">*</span>
                </label>
                <select v-model="form.gender" class="form-select w-full">
                  <option value="">Select...</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div class="w-full lg:w-6/12 px-4 mb-3">
                <label class="field-label">
                  Civil Status <span class="text-red-500">*</span>
                </label>
                <select v-model="form.civil_status" class="form-select w-full">
                  <option value="">Select...</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </select>
              </div>

              <div class="w-full lg:w-6/12 px-4 mb-3">
                <label class="field-label">
                  CA Driver's License Number
                  <span class="text-teal-400">(if any)</span>
                </label>
                <input
                  v-model="form.ca_driver_license_number"
                  type="text"
                  class="form-input-primary w-full"
                  placeholder="CA Driver's License Number"
                />
              </div>

              <div class="w-full px-4 mt-4 mb-2">
                <h6 class="subsection-title">Address Information</h6>
              </div>

              <div class="w-full lg:w-12/12 px-4 mb-3">
                <label class="field-label">
                  Address <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.address"
                  type="text"
                  class="form-input-primary w-full"
                  placeholder="Street Address"
                />
              </div>

              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">
                  City <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.city"
                  type="text"
                  class="form-input w-full"
                />
              </div>

              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">
                  State <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.state"
                  type="text"
                  class="form-input w-full"
                />
              </div>

              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">
                  Zip Code <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.zip_code"
                  type="text"
                  class="form-input w-full"
                />
              </div>

              <div class="w-full lg:w-4/12 px-4 mb-3">
                <label class="field-label">
                  Country <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.country"
                  type="text"
                  class="form-input w-full"
                />
              </div>

              <div class="w-full px-4 mt-4 mb-2">
                <h6 class="subsection-title">Regional Center Information</h6>
              </div>

              <div class="w-full lg:w-3/12 px-4 mb-3">
                <label class="field-label">Regional Center Name</label>
                <input
                  v-model="form.regional_center"
                  type="text"
                  class="form-input-primary w-full"
                  placeholder="Regional Center Name"
                />
              </div>

              <div class="w-full lg:w-3/12 px-4 mb-3">
                <label class="field-label">Service Coordinator Name</label>
                <input
                  v-model="form.regional_center_coordinator"
                  type="text"
                  class="form-input-primary w-full"
                  placeholder="Service Coordinator Name"
                />
              </div>

              <div class="w-full lg:w-3/12 px-4 mb-3">
                <label class="field-label">Service Coordinator Email</label>
                <input
                  v-model="form.coordinator_email"
                  type="email"
                  class="form-input-primary w-full"
                  placeholder="Service Coordinator Email"
                />
              </div>

              <div class="w-full lg:w-3/12 px-4 mb-3">
                <label class="field-label">Service Coordinator Phone Number</label>
                <input
                  v-model="form.coordinator_number"
                  v-mask="'(###) ###-####'"
                  type="text"
                  class="form-input-primary w-full"
                  placeholder="(123) 456-7890"
                />
              </div>
            </div>

            <div class="w-full px-4 mt-6">
              <div class="trigger-card">
                <div class="trigger-title">
                  Is the client under 18 years old or represented by a legal guardian?
                </div>
                <div class="trigger-options">
                  <label class="radio-pill">
                    <input
                      type="radio"
                      value="yes"
                      v-model="form.is_client_minor_or_guarded"
                      @change="handleGuardianToggle"
                    />
                    <span>Yes</span>
                  </label>
                  <label class="radio-pill">
                    <input
                      type="radio"
                      value="no"
                      v-model="form.is_client_minor_or_guarded"
                      @change="handleGuardianToggle"
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
            </div>

            <div v-if="showRepresentativeSection" class="mt-6">
              <h6 class="section-title">Employer / Legal Representative Information</h6>

              <div class="flex flex-wrap">
                <div class="w-full lg:w-4/12 px-4 mb-3">
                  <label class="field-label">
                    Relationship to Client <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="form.relationship_to_client"
                    class="form-select w-full"
                  >
                    <option value="">Select...</option>
                    <option value="Parent">Parent</option>
                    <option value="Conservator">Conservator</option>
                    <option value="Power of Attorney">Power of Attorney</option>
                  </select>
                </div>

                <div class="w-full lg:w-4/12 px-4 mb-3">
                  <label class="field-label">
                    Legal Representative First Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.legal_representative_first_name"
                    type="text"
                    class="form-input-primary w-full"
                    placeholder="Legal Representative First Name"
                  />
                </div>

                <div class="w-full lg:w-4/12 px-4 mb-3">
                  <label class="field-label">
                    Legal Representative Last Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.legal_representative_last_name"
                    type="text"
                    class="form-input-primary w-full"
                    placeholder="Legal Representative Last Name"
                  />
                </div>

                <div class="w-full lg:w-4/12 px-4 mb-3">
                  <label class="field-label">
                    Title <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.title"
                    type="text"
                    class="form-input-primary w-full"
                    placeholder="eg. Household Employer"
                  />
                </div>

                <div class="w-full lg:w-4/12 px-4 mb-3">
                  <label class="field-label">
                    EIN Number <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.ein_number"
                    v-mask="'##-#######'"
                    type="text"
                    maxlength="10"
                    class="form-input-primary w-full"
                    placeholder="12-3456789"
                  />
                </div>

                <div class="w-full lg:w-4/12 px-4 mb-3">
                  <label class="field-label">
                    EIN Payer Full Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.ein_number_payer_full_name"
                    type="text"
                    class="form-input-primary w-full"
                    placeholder="EIN Payer Full Name"
                  />
                </div>

                <div class="w-full px-4 mt-4 mb-2">
                  <h6 class="subsection-title">Proof of Legal Representation</h6>
                  <p class="helper-text">
                    Upload a document such as POA, conservatorship paperwork, disability documentation,
                    or any document that helps verify this person can represent the client.
                  </p>
                </div>

                <div class="w-full lg:w-12/12 px-4 mb-3">
                  <label class="field-label">
                    Upload / Select File <span class="text-red-500">*</span>
                  </label>

                  <div class="upload-box">
                    <input
                      ref="guardianProofInput"
                      type="file"
                      class="hidden"
                      @change="handleGuardianProofFileChange"
                    />

                    <button
                      type="button"
                      class="upload-btn"
                      @click="$refs.guardianProofInput.click()"
                    >
                      Choose File
                    </button>

                    <div class="upload-file-name">
                      {{ guardianProofDisplayName }}
                    </div>
                  </div>

                  <p v-if="existingGuardianProofUrl" class="helper-text mt-2">
                    Existing file saved for ID 2:
                    <a
                      :href="existingGuardianProofUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="existing-proof-link"
                    >
                      View current document
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <h6 class="section-title mt-6">General Contact Info</h6>

            <div class="flex flex-wrap">
              <div class="w-full lg:w-6/12 px-4 mb-3">
                <label class="field-label">
                  Phone Number (Optional)
                </label>
                <input
                  v-model="form.contact_number"
                  v-mask="'(###) ###-####'"
                  type="text"
                  placeholder="(123) 456-1234"
                  class="form-input w-full"
                />
              </div>

              <div class="w-full lg:w-6/12 px-4 mb-3">
                <label class="field-label">
                  Email <span class="text-red-400">*</span>
                </label>
                <input
                  v-model="form.email"
                  type="email"
                  class="form-input w-full"
                />
              </div>

              <div class="w-full px-4 mb-3 text-center mt-4">
                <button
                  v-if="!loading"
                  class="bg-lightBlue-500 text-white active:bg-lightBlue-600 font-bold uppercase text-xs px-6 py-3 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
                  type="submit"
                >
                  Save Update Information
                </button>

                <svg
                  v-if="loading"
                  class="animate-spin inline-block h-5 w-5 text-lightBlue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>

                <span class="ml-2">{{ loading ? "Updating..." : "" }}</span>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: "UpdateClientForm",
  data() {
    return {
      form: {
        id: null,
        client_code: "",
        first_name: "",
        last_name: "",
        middle_name: "",
        ss_number: "",
        ein_number: "",
        uci_number: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        contact_number: "",
        email: "",
        gender: "",
        country: "",
        ein_number_payer_full_name: "",
        ca_driver_license_number: "",
        coordinator_email: "",
        coordinator_number: "",
        regional_center: "",
        regional_center_coordinator: "",
        civil_status: "",
        date_of_birth: "",
        title: "",
        is_client_minor_or_guarded: "no",
        relationship_to_client: "",
        legal_representative_first_name: "",
        legal_representative_last_name: "",
      },
      guardianProofFile: null,
      guardianProofDisplayName: "No file selected",
      existingGuardianProofUrl: "",
      loading: false,
    };
  },
  computed: {
    showRepresentativeSection() {
      return this.form.is_client_minor_or_guarded === "yes";
    },
  },
  methods: {
    handleGuardianToggle() {
      if (this.form.is_client_minor_or_guarded === "yes" && !this.form.title) {
        this.form.title = "Household Employer";
      }
    },

    handleGuardianProofFileChange(event) {
      const file =
        event.target.files && event.target.files[0]
          ? event.target.files[0]
          : null;

      this.guardianProofFile = file;
      this.guardianProofDisplayName = file ? file.name : "No file selected";
    },

    async updateClient() {
      try {
        this.loading = true;
        const token = localStorage.getItem("token");

        const formData = new FormData();

        Object.keys(this.form).forEach((key) => {
          formData.append(key, this.form[key] ?? "");
        });

        if (this.showRepresentativeSection && this.guardianProofFile) {
          formData.append("guardian_proof_file", this.guardianProofFile);
        }

        const response = await fetch(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/update_client_information`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        const result = await response.json();

        if (response.status === 422) {
          const errorMessages = Object.values(result.errors)
            .flat()
            .join("<br>");
          Swal.fire({
            title: "Complete the missing fields",
            html: errorMessages,
            icon: "warning",
          });
          return;
        }

        if (!response.ok || result.status === "error") {
          Swal.fire(
            "Error",
            result.message || "Failed to update information.",
            "error"
          );
          return;
        }

        if (result.client && result.client.valid_id_2_url) {
          this.existingGuardianProofUrl = result.client.valid_id_2_url;
          if (!this.guardianProofFile) {
            this.guardianProofDisplayName = "Current file saved";
          }
        }

        Swal.fire("Success", "Information was updated successfully", "success");
        this.$emit("client-updated");
      } catch (error) {
        console.error(error);
        Swal.fire(
          "Warning",
          "Something went wrong. Please try again.",
          "warning"
        );
      } finally {
        this.loading = false;
      }
    },

    async fetchInformation() {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch_client_information`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const result = await response.json();

        if (result.client) {
          this.form = {
            ...this.form,
            ...result.client,
            ein_number: result.client.ein_number
              ? String(result.client.ein_number)
              : "",
            is_client_minor_or_guarded:
              result.client.is_client_minor_or_guarded || "no",
            relationship_to_client:
              result.client.relationship_to_client || "",
            legal_representative_first_name:
              result.client.legal_representative_first_name || "",
            legal_representative_last_name:
              result.client.legal_representative_last_name || "",
          };

          this.existingGuardianProofUrl = result.client.valid_id_2_url || "";

          if (this.existingGuardianProofUrl) {
            this.guardianProofDisplayName = "Current file saved";
          }

          if (
            this.form.is_client_minor_or_guarded === "yes" &&
            !this.form.title
          ) {
            this.form.title = "Household Employer";
          }
        }
      } catch (error) {
        console.error("Error fetching personal information:", error);
      }
    },
  },
  mounted() {
    this.fetchInformation();
  },
};
</script>

<style scoped>
.section-title {
  color: #94a3b8;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.subsection-title {
  color: #64748b;
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.field-label {
  display: block;
  text-transform: uppercase;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.form-input-primary,
.form-input,
.form-select {
  border: 0;
  padding: 0.75rem 0.875rem;
  color: #475569;
  background: #ffffff;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
  outline: none;
  transition: all 0.15s ease;
}

.form-input-primary:focus,
.form-input:focus,
.form-select:focus {
  box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.2);
}

.trigger-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem 1rem;
}

.trigger-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 0.9rem;
}

.trigger-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.radio-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: #ffffff;
  border: 1px solid #dbeafe;
  border-radius: 9999px;
  padding: 0.6rem 0.95rem;
  color: #334155;
  font-weight: 600;
  cursor: pointer;
}

.radio-pill input {
  accent-color: #0ea5e9;
}

.upload-box {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  padding: 0.85rem 1rem;
}

.upload-btn {
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 0.6rem;
  padding: 0.65rem 1rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
}

.upload-file-name {
  color: #475569;
  font-size: 0.85rem;
  word-break: break-word;
}

.helper-text {
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.45;
}

.existing-proof-link {
  color: #0284c7;
  font-weight: 700;
  text-decoration: underline;
}

@media (max-width: 767px) {
  .trigger-options {
    flex-direction: column;
  }

  .radio-pill {
    width: 100%;
    justify-content: flex-start;
  }

  .upload-box {
    align-items: flex-start;
  }

  .upload-btn {
    width: 100%;
  }

  .upload-file-name {
    width: 100%;
  }
}
</style>
